import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

export function PartnerAdminPanel() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    planName: "Small Team",
    maxCoaches: 10,
    pricePerMonth: 1499,
    pricePerYear: 14999,
    logoUrl: "",
    email: "",
  });
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [coachIdToAssign, setCoachIdToAssign] = useState("");
  const [stats, setStats] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await api("/api/partners");
      setPartners(rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function createPartner() {
    setError(null);
    try {
      await api("/api/partners", { method: "POST", body: JSON.stringify(form) });
      setForm({
        name: "",
        planName: "Small Team",
        maxCoaches: 10,
        pricePerMonth: 1499,
        pricePerYear: 14999,
        logoUrl: "",
        email: "",
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function getStats() {
    if (selectedPartnerId === "") return;
    try {
      const s = await api(`/api/partners/${selectedPartnerId}/stats`);
      setStats(s);
    } catch (e) {
      setError(e.message);
    }
  }

  async function assignCoach() {
    if (selectedPartnerId === "" || coachIdToAssign === "") return;
    try {
      await api(`/api/partners/${selectedPartnerId}/assign-coach/${coachIdToAssign}`, { method: "POST" });
      await getStats();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function removeCoach() {
    if (selectedPartnerId === "" || coachIdToAssign === "") return;
    try {
      await api(`/api/partners/${selectedPartnerId}/remove-coach/${coachIdToAssign}`, { method: "DELETE" });
      await getStats();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <h2 className="text-2xl font-bold mb-4">Partner Admin</h2>

      {error && <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="rounded-2xl border p-4">
          <h3 className="font-semibold mb-3">Create Partner</h3>
          <div className="grid grid-cols-2 gap-3">
            <input
              className="border rounded-lg px-3 py-2 col-span-2"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2 col-span-2"
              placeholder="Logo URL"
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2 col-span-2"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Plan Name"
              value={form.planName}
              onChange={(e) => setForm({ ...form, planName: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Max Coaches"
              type="number"
              value={form.maxCoaches}
              onChange={(e) => setForm({ ...form, maxCoaches: Number(e.target.value) })}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Price / month"
              type="number"
              value={form.pricePerMonth}
              onChange={(e) => setForm({ ...form, pricePerMonth: Number(e.target.value) })}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Price / year"
              type="number"
              value={form.pricePerYear}
              onChange={(e) => setForm({ ...form, pricePerYear: Number(e.target.value) })}
            />
          </div>
          <button onClick={createPartner} className="mt-4 w-full rounded-xl py-2 border bg-black text-white">
            Save Partner
          </button>
        </div>

        <div className="rounded-2xl border p-4">
          <h3 className="font-semibold mb-3">Seat Management</h3>
          <div className="grid grid-cols-2 gap-3">
            <select
              className="border rounded-lg px-3 py-2 col-span-2"
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">Select partner…</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (seats {p.coachCount}/{p.maxCoaches})
                </option>
              ))}
            </select>
            <input
              className="border rounded-lg px-3 py-2"
              type="number"
              placeholder="Coach ID"
              value={coachIdToAssign}
              onChange={(e) => setCoachIdToAssign(e.target.value === "" ? "" : Number(e.target.value))}
            />
            <button onClick={assignCoach} className="rounded-xl py-2 border bg-black text-white">
              Assign
            </button>
            <button onClick={removeCoach} className="rounded-xl py-2 border">
              Remove
            </button>
            <button onClick={getStats} className="rounded-xl py-2 border col-span-2">
              Refresh Stats
            </button>
          </div>
          {stats && (
            <div className="mt-4 text-sm text-gray-700">
              <div>
                <b>{stats.name}</b> • Plan: {stats.planName} • Seats: {stats.coachesTotal}/{stats.maxCoaches} • Remaining:{" "}
                {stats.remainingSeats}
              </div>
              <div>
                Status: {stats.isActive ? "Active" : "Inactive"}{" "}
                {stats.currentPeriodEndUtc ? `• Renews: ${new Date(stats.currentPeriodEndUtc).toLocaleDateString()}` : ""}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-3">Partners</h3>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4">Seats used</th>
                  <th className="py-2 pr-4">Active</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="py-2 pr-4">{p.id}</td>
                    <td className="py-2 pr-4">{p.name}</td>
                    <td className="py-2 pr-4">{p.planName}</td>
                    <td className="py-2 pr-4">
                      {p.coachCount}/{p.maxCoaches}
                    </td>
                    <td className="py-2 pr-4">{p.isActive ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
